(() => {
  if (customElements.get('inutec-shipping-estimator')) return;
  class InutecShippingEstimator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.country = this.querySelector('[data-shipping-country]');
      this.province = this.querySelector('[data-shipping-province]');
      this.zip = this.querySelector('[data-shipping-zip]');
      this.button = this.querySelector('[data-shipping-calculate]');
      this.result = this.querySelector('[data-shipping-result]');
      this.messages = JSON.parse(this.querySelector('[data-shipping-messages]').textContent);
      this.root = this.dataset.root || '/';
      if (!this.root.endsWith('/')) this.root += '/';
      const selected = [...this.country.options].find(o => o.value === this.dataset.defaultCountry || o.textContent.trim() === this.dataset.defaultCountry);
      if (selected) this.country.value = selected.value;
      this.updateProvinces(this.dataset.defaultProvince);
      this.country.addEventListener('change', () => { this.updateProvinces(); this.invalidate(); });
      this.province.addEventListener('change', () => this.invalidate());
      this.zip.addEventListener('input', () => this.invalidate());
      this.button.addEventListener('click', () => this.calculate());
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('input, select')) { event.preventDefault(); this.calculate(); }
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.invalidate(true));
      this.cartChanged = () => this.invalidate(true);
      document.addEventListener('cart:updated', this.cartChanged);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      document.removeEventListener('cart:updated', this.cartChanged);
    }
    updateProvinces(preferred) {
      let provinces = [];
      try { provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) {}
      this.province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value)));
      if (preferred && [...this.province.options].some(o => o.value === preferred)) this.province.value = preferred;
      this.querySelector('[data-shipping-province-group]').hidden = !provinces.length;
      this.province.disabled = !provinces.length;
    }
    invalidate(cartChanged = false) {
      this.controller?.abort();
      this.setLoading(false);
      if (this.result.textContent) this.result.textContent = cartChanged ? this.messages.changed : '';
    }
    setLoading(loading) {
      this.button.disabled = loading;
      this.classList.toggle('is-loading', loading);
      this.setAttribute('aria-busy', String(loading));
    }
    fingerprint(cart) {
      return JSON.stringify({currency:cart.currency,total:cart.total_price,items:cart.items.map(i => [i.key,i.quantity,i.final_line_price])});
    }
    async getJSON(url, options, signal) {
      const response = await fetch(url, {...options,credentials:'same-origin',headers:{'Accept':'application/json',...(options?.headers || {})},signal});
      if (!response.ok) throw new Error(response.status === 422 ? 'address' : 'error');
      return response.json();
    }
    async calculate() {
      if (!this.country.value || this.country.value === '---' || !this.zip.value.trim()) {
        this.result.textContent = this.messages.address;
        if (!this.zip.value.trim()) this.zip.focus();
        return;
      }
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort('timeout'), 22000);
      const signal = controller.signal;
      this.setLoading(true);
      this.result.textContent = this.messages.loading;
      try {
        const cart = await this.getJSON(this.root + 'cart.js', {}, signal);
        if (!cart.item_count) { this.result.textContent = this.messages.empty; return; }
        const before = this.fingerprint(cart);
        const params = new URLSearchParams({'shipping_address[country]':this.country.value,'shipping_address[zip]':this.zip.value.trim()});
        if (!this.province.disabled && this.province.value) params.set('shipping_address[province]',this.province.value);
        const query = '?' + params.toString();
        await this.getJSON(this.root + 'cart/prepare_shipping_rates.json' + query, {method:'POST'}, signal);
        let payload = null;
        for (let attempt = 0; attempt < 10; attempt++) {
          if (attempt) await new Promise((resolve,reject) => {
            const cancel = () => { clearTimeout(timer); reject(new DOMException('Aborted','AbortError')); };
            const timer = setTimeout(() => { signal.removeEventListener('abort',cancel); resolve(); },1200);
            signal.addEventListener('abort',cancel,{once:true});
          });
          payload = await this.getJSON(this.root + 'cart/async_shipping_rates.json' + query, {}, signal);
          if (payload !== null) break;
        }
        if (payload === null) throw new Error('error');
        const current = await this.getJSON(this.root + 'cart.js', {}, signal);
        if (before !== this.fingerprint(current)) { this.result.textContent = this.messages.changed; return; }
        const rates = (payload.shipping_rates || []).filter(r => r.price !== null && r.price !== undefined && String(r.price).trim() !== '' && Number.isFinite(Number(r.price)) && Number(r.price) >= 0 && !/pickup|pick.?up|collection|abholung|avhämtning/i.test(r.name || ''));
        if (!rates.length) { this.result.textContent = this.messages.no_rates; return; }
        const currencies = new Set(rates.map(r => r.currency || cart.currency || this.dataset.currency));
        if (currencies.size !== 1) throw new Error('error');
        rates.sort((a,b) => Number(a.price)-Number(b.price));
        const rate = rates[0];
        const price = Number(rate.price);
        const currency = rate.currency || cart.currency || this.dataset.currency;
        const destination = this.country.value;
        const parcelCountries = ['Austria','Belgium','Czechia','Czech Republic','Denmark','France','Luxembourg','Netherlands','Poland'];
        if (currency === 'EUR' && cart.total_weight > 30000 && ((destination === 'Germany' && Math.abs(price-9.9)<0.001) || (parcelCountries.includes(destination) && Math.abs(price-14.9)<0.001))) {
          this.result.textContent = this.messages.check_rate;
          return;
        }
        const formatted = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'de',{style:'currency',currency}).format(price);
        this.result.textContent = this.messages.from.replace('[price]',formatted);
      } catch (error) {
        if (signal.aborted && signal.reason !== 'timeout') return;
        this.result.textContent = this.messages[error.message] || this.messages.error;
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) this.setLoading(false);
      }
    }
  }
  customElements.define('inutec-shipping-estimator', InutecShippingEstimator);
})();
